import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CardNumberElement, CardCvcElement, CardExpiryElement } from '@stripe/react-stripe-js';
import { Grid, TextField, useTheme } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import get from 'lodash/get';

// STYLES
import { CardFieldsStyled, getCardStyle, SInfoIconButton, SCVCTooltip } from './styles';

// HELP
import StripeInput from './help/StripeInput';
import CardElementField from './help/CardElementField';

function getFieldData(fieldName, domain, field, opts = { value: false, helperText: false }) {
  const name = domain ? `${domain}.${fieldName}` : fieldName;

  const touched = get(field.touched, name);
  const error = get(field.errors, name);
  const value = opts.value ? get(field.values, name) : undefined;
  const helperText = opts.helperText ? touched && error : undefined;

  return { name, error: Boolean(touched && error), touched, helperText, value };
}

export function StripeCardFields({
  setFieldValue,
  setFieldTouched,
  name,
  errors,
  touched,
  className,
  values,
}) {
  const [t] = useTranslation();
  const theme = useTheme();

  const options = getCardStyle(theme);
  const formData = { touched, values, errors };
  const numFieldData = getFieldData('ccnumber', name, formData);
  const expFieldData = getFieldData('ccexp', name, formData);
  const cvcFieldData = getFieldData('cvc', name, formData);
  const zipFieldData = getFieldData('zipcode', name, formData, { value: true, helperText: true });

  return (
    <CardFieldsStyled className={className}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CardElementField
            {...numFieldData}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            label={t('inputs.creditCardNum')}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputComponent: StripeInput,
              inputProps: {
                options: { ...options, showIcon: true },
                component: CardNumberElement,
              },
            }}
          />
        </Grid>
        <Grid item xs>
          <CardElementField
            {...expFieldData}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            options={options}
            label={t('inputs.expDate_short')}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputComponent: StripeInput,
              inputProps: {
                options,
                component: CardExpiryElement,
              },
            }}
          />
        </Grid>
        <Grid item xs="auto">
          <div className="cvc-wrapper">
            <CardElementField
              {...cvcFieldData}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              options={options}
              label={t('inputs.cvc')}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                inputComponent: StripeInput,
                inputProps: {
                  options,
                  component: CardCvcElement,
                },
              }}
            />
            <SCVCTooltip
              title={<p className="p4 cvc-info">{t('inputs.cvc_info')}</p>}
              placement="top"
              enterTouchDelay={0}
              leaveTouchDelay={3000}
            >
              <SInfoIconButton aria-label="info" tabIndex="-1">
                <InfoIcon fontSize="large" />
              </SInfoIconButton>
            </SCVCTooltip>
          </div>
        </Grid>
        <Grid item xs={12} sm={12}>
          <TextField
            {...zipFieldData}
            onChange={({ target }) => setFieldValue(target.name, target.value)}
            onBlur={({ target }) => {
              if (!zipFieldData.touched) {
                setFieldTouched(target.name, true, false);
              }
            }}
            label={t('inputs.zipcode')}
            placeholder=""
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </CardFieldsStyled>
  );
}

StripeCardFields.defaultProps = {
  errors: {},
  touched: {},
  values: {},
};

StripeCardFields.propTypes = {
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  className: PropTypes.string,
  name: PropTypes.string,
  errors: PropTypes.object,
  touched: PropTypes.object,
  values: PropTypes.object,
};

export default React.memo(StripeCardFields);
